import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ReactComponent as DeleteIcon } from '../../../assets/icons/delete-icon.svg';
import { ReactComponent as EditIcon } from '../../../assets/icons/edit.svg';
import { ReactComponent as RolesIcon } from '../../../assets/icons/sidebar/roles-icon.svg';
// import { ReactComponent as ExpandIcon } from '../../../assets/icons/tables/expand-icon.svg';
import { ReactComponent as ActiveUserIcon } from '../../../assets/icons/user-icon-active.svg';
import DefaultApiLimit from '../../../config/defaultLimit';
// import { ApiRequest } from '../../../interface/common.interface';
import { RequestRoleInterFace, RoleInterFace } from '../../../interface/role-management.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import {
  disableRolesFilterApplied,
  fetchAllRoles,
  fetchRolesFilters,
  updateRolesFilters,
  setSortData,
} from '../../../redux/slices/RolesManagementSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import DeleteWarning from '../../../shared-components/delete-warning/DeleteWarning';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import RoleSidePanel from '../../../shared-components/table-side-panel/RoleSidePanel';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../shared-functions/ConvertUTCtoDateTime';
import { removeEmptyKeys } from '../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import rolesApi from '../../../utils/api/roles';

import AddRole from './add-role';

interface ModalState {
  status: boolean;
  title: string;
  header: boolean;
  content: JSX.Element;
  rightCorner: string;
  notification: boolean;
}

/**
 * User Management page function component
 * @returns {any} Roles list component
 */
function RolesList() {
  const { t } = useTranslation('userRole');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    roles,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortField,
    sortBy,
  } = useAppSelector((state) => state.roles);

  const [modalView, setModalView] = useState<ModalState>({
    status: false,
    title: '',
    header: true,
    content: <div />,
    rightCorner: '',
    notification: false,
  });
  const [searchParameters, setSearchParameters] = useSearchParams();
  const [roleToEdit, setRoleToEdit] = useState<RoleInterFace | undefined>(undefined);
  const [addRoleOpen, setAddRoleOpen] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);

  /**
   * Modal box close function
   */
  const closeModal = () => {
    setModalView({
      status: false,
      title: '',
      header: true,
      content: <div />,
      rightCorner: '',
      notification: false,
    });
    setAddRoleOpen(false);
    setRoleToEdit(undefined);
  };

  /**
   * Fetch Users list function
   * @param {RequestRoleInterFace | any} data - The data
   */
  const fetchData = (data: RequestRoleInterFace | any) => {
    if (!loading) {
      dispatch(fetchAllRoles(data));
    }
  };

  /**
   * Handle add action function
   */
  const handleAddAction = () => {
    navigate('../role/add');
  };

  /**
   * Completed the update task
   * @param {string} message - Message
   */
  const completedTask = (message: string) => {
    fetchData({
      page,
      limit,
    });
    setAddRoleOpen(false);
    setRoleToEdit(undefined);
    setModalView({
      status: true,
      title: '',
      header: false,
      content: (
        <SuccessNotification
          title={message}
          description=""
          notificationCloseTimer={3000}
          onNotificationClose={closeModal}
        />
      ),
      rightCorner: '',
      notification: true,
    });
  };

  /**
   * User details view function
   * @param {any} currentData - The currentData
   */
  const viewFunction = (currentData: string) => {
    const roleDetail: RoleInterFace | undefined = roles.find(
      (value: RoleInterFace) => value.id === currentData,
    );
    setRoleToEdit(roleDetail);
    setAddRoleOpen(true);
  };
  const column = [
    {
      label: t('date'),
      accessorKey: 'createdAt' as keyof RoleInterFace,
      sortAllow: true,
    },
    {
      label: t('roleName'),
      accessorKey: 'name' as keyof RoleInterFace,
      sortAllow: true,
      queryParamsKey: 'roleId',
      sidePanelComponent: <RoleSidePanel fetchIdFrom="queryParams" onEdit={viewFunction} />,
    },
    {
      label: t('responsibility'),
      accessorKey: 'responsibility' as keyof RoleInterFace,
      sortAllow: true,
    },
    // {
    //   label: t('active'),
    //   accessorKey: '' as keyof RoleInterFace,
    //   columnWidth: 60,
    //   customData:
    //     /**
    //      * function for action buttons
    //      * @param currentData
    //      * @returns
    //      */
    //     (currentData: RoleInterFace) => (
    //       <div className="action-button-group">
    //         <div className="device-listing-actions-wrap">
    //           <span role="presentation" onClick={() => viewFunction(currentData.id)}>
    //             <EditPencil />
    //           </span>
    //         </div>
    //       </div>
    //     ),
    // },
  ];

  /**
   * Column definitions for the table
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('date'), accessorKey: 'createdAt' },
    { label: t('roleName'), accessorKey: 'name' },
    { label: t('responsibility'), accessorKey: 'responsibility' },
    { label: t('created', 'Created'), accessorKey: 'createdAt' },
    { label: t('lastUpdated', 'Last Updated'), accessorKey: 'updatedAt' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  const [exportLoader, setExportLoader] = useState<boolean>(false);

  /**
   * Table pagination function
   * @param {RequestRoleInterFace} value - Value
   */
  const pageNateLimitChange = (value: RequestRoleInterFace) => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    fetchData({
      ...queryParameters,
      ...value,
      sortField,
      sortBy,
    });
  };

  /**
   * Export data function
   * @param {any} type - The type
   */
  const exportDataPdfOrCSV = (type: string) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload: any = {
      ...queryParameters,
    };
    if (sortBy) payload.sortBy = sortBy;
    if (sortField) payload.sortField = sortField;

    setExportLoader(true);
    rolesApi
      .exportRoles(removeEmptyKeys(payload))
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: RoleInterFace, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'createdAt':
                return row.createdAt ? ConvertUTCtoDateTime(row.createdAt) : 'N/A';
              case 'updatedAt':
                return row.updatedAt ? ConvertUTCtoDateTime(row.updatedAt) : '-';
              case 'name':
                return row.name || 'N/A';

              case 'responsibility':
                return row.responsibility || 'N/A';

              default:
                return (row as any)[col.accessorKey] || 'N/A';
            }
          }),
        );
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'roles-list.csv');
          } else {
            exportPDF(headers, rows, 'roles-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to open side panel on row click
   * @param {any} roleId - The roleId
   */
  const handleOpenSidePanelOnRowClick = (roleId: string) => {
    const newParameters = new URLSearchParams(searchParameters);
    newParameters.set('roleId', roleId);
    setSearchParameters(newParameters);
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page,
      limit,
      sortField,
      sortBy,
    });
  };

  /**
   * Function to delete role
   * @param {string} roleId - The roleId
   * @returns {void}
   */
  const handleDeleteRole = (roleId: string) => {
    setModalView({
      status: true,
      title: '',
      header: false,
      content: (
        <DeleteWarning
          selectedRowId={roleId}
          title={t('deleteRole') || 'Delete Role'}
          description={t('deleteRoleConfirmation') || 'Are you sure you want to delete this role?'}
          cancelButtonOnClick={closeModal}
          submitButtonOnClick={(id) => {
            if (id) {
              rolesApi
                .deleteRoles(id)
                .then(() => {
                  completedTask(t('roleDeletedSuccessfully') || 'Role deleted successfully');

                  // Clear roleId parameter if it matches the deleted role
                  const parameters = new URLSearchParams(window.location.search);
                  if (parameters.get('roleId') === id) {
                    parameters.delete('roleId');
                    setSearchParameters(parameters, { replace: true });
                  }
                })
                .catch((error) => {
                  closeModal();
                  dispatch(
                    setNotificationMessage({
                      message: error?.response?.data?.message || t('somethingWentWrong'),
                      status: true,
                      type: 'error',
                      code: error?.response?.data?.code,
                    }),
                  );
                });
            }
          }}
        />
      ),
      rightCorner: '',
      notification: true,
    });
  };

  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
      });
      return;
    }

    if (isFilterUpdated) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
      });
      dispatch(disableRolesFilterApplied());
    }
  }, [
    filters,
    isFilterUpdated,
    limit,
    dispatch,
  ]);

  useEffect(() => {
    dispatch(
      setPageTitle({
        title: t('roles'),
        icon: <RolesIcon />,
        count: totalResults,
      }),
    );
  }, [dispatch, t, totalResults]);

  /**
   * Renders the side panel top-left content including title and edit action button.
   * @returns {any} The top-left header JSX element, or undefined if no roleId is present in URL.
   */
  const renderSidePanelTopLeftContent = () => {
    const roleId = searchParameters.get('roleId');
    if (!roleId) {
      return undefined;
    }
    const selectedRole = roles.find((r: any) => r._id === roleId);

    return (
      <div className="side-panel-header-left-title-actions">
        <p className="side-panel-header-title">{t('roleDetails')}</p>
        <div className="side-panel-header-left-icons">
          {permissionCheck('userRoleManagementUpdate') && (
            <div className="button-wrap">
              <span
                role="presentation"
                className="access-facility"
                onClick={() => {
                  navigate(`../role/edit/${roleId}`, { state: selectedRole });
                }}>
                <EditIcon className="icon" />
              </span>
              <div className="hover-label">{t('edit') || 'Edit'}</div>
            </div>
          )}

          {permissionCheck('userRoleManagementDelete') && (
            <div className="button-wrap">
              <span
                role="presentation"
                className="access-facility"
                onClick={() => handleDeleteRole(roleId)}>
                <DeleteIcon className="icon" />
              </span>
              <div className="hover-label">{t('delete') || 'Delete'}</div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="table-content">
      <ModalBox
        open={modalView.status}
        title={modalView.title}
        content={modalView.content}
        closeModal={closeModal}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
        notification={modalView.notification}
      />
      <CustomTable
        dataKey="RoleManagement"
        sidePanelTopLeftContent={renderSidePanelTopLeftContent()}
        header={column}
        data={roles}
        serialNo
        version="v2"
        actionButton
        loading={loading}
        page={page}
        limit={limit}
        totalPages={totalPages}
        totalResults={totalResults}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageNateLimitChange}
        onHiddenColumnsChange={setHiddenColumns}
        sizeChangeButtonRequired
        filters={filters}
        filtersToDispatch={updateRolesFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchRolesFilters}
        rowClickEvent={handleOpenSidePanelOnRowClick}
        rowClickParamsKey="roleId"
        exportDataPdfOrCSV={
          permissionCheck('userRoleManagementExport') ? exportDataPdfOrCSV : undefined
        }
        created
        lastUpdated
        exportLoader={exportLoader}
        rightTopContent={
          <div className="table-bottom-button-list">
            <div className="right-action">
              <div className="table-top-button-list">
                <div className="table-button-item">
                  <MyButton
                    label={t('addRole')}
                    buttonType="submit"
                    onClickFunc={handleAddAction}
                    svgIcon={<ActiveUserIcon />}
                    disableButton={!permissionCheck('userRoleManagementAdd')}
                  />
                </div>
              </div>
            </div>
          </div>
        }
        sidePanelEnabled={addRoleOpen}
        sidePanelManualContent={
          <AddRole
            closeModal={() => setAddRoleOpen(false)}
            roleDetail={roleToEdit}
            completedTask={completedTask}
          />
        }
        onSidePanelClose={() => setAddRoleOpen(false)}
        refreshRequired
        handleRefresh={handleRefresh}
        refershCurrentPages={handleRefresh}
        hoverLabel="Refresh"
      />
    </div>
  );
}

export default RolesList;
